import { hasDisplayValue } from "./fieldValueDisplay";

const CREDENTIAL_SUBJECT_SKIP_KEYS = new Set([
  "id",
  "type",
  "email",
  "tipo_de_documento_code",
  "credential_id",
]);

export const APOSTILLE_CREDENTIAL_FIELDS = [
  {
    key: "nome_do_pais",
    label: "apostille_field_1",
  },
  {
    key: "nome_do_signatario_do_registo_civil",
    label: "apostille_field_2",
  },
  {
    key: "cargo_do_signatario_do_registo_civil",
    label: "apostille_field_3",
  },
  {
    key: "tipo_de_selo",
    label: "apostille_field_4",
  },
  {
    key: "nota_local_apostila",
    label: "apostille_field_5",
  },
  {
    key: "autoridade_emissora_da_apostila",
    label: "apostille_field_7",
  },
  {
    key: "selo_carimbo",
    label: "apostille_field_9",
  },
  {
    key: "assinatura",
    label: "apostille_field_10",
  },
];

export const APOSTILLE_DISPLAY_LABELS = new Set([
  ...APOSTILLE_CREDENTIAL_FIELDS.map((field) => field.label),
  "apostille_field_6",
  "apostille_field_8",
]);

export function isApostilleCredentialSubject(subject) {
  if (!subject || typeof subject !== "object" || Array.isArray(subject)) {
    return false;
  }

  return APOSTILLE_CREDENTIAL_FIELDS.some(({ key }) =>
    hasDisplayValue(subject[key]),
  );
}

export function isApostilleDisplayLabel(label) {
  return (
    APOSTILLE_DISPLAY_LABELS.has(label) ||
    label.startsWith("__section__") ||
    label.startsWith("__pair__")
  );
}

export function isCredentialSubjectEmpty(subject) {
  if (subject == null) {
    return true;
  }

  if (typeof subject === "string") {
    return subject.trim() === "";
  }

  if (typeof subject !== "object" || Array.isArray(subject)) {
    return true;
  }

  return !Object.values(subject).some(hasDisplayValue);
}

export function isCredentialSubjectUnavailable(responseData) {
  if (responseData?.is_payment_required || responseData?.claim169) {
    return false;
  }

  return (
    responseData?.credential_subject !== undefined &&
    isCredentialSubjectEmpty(responseData.credential_subject)
  );
}

export function getDisplayCredentialId(responseData) {
  const subject = responseData?.credential_subject;

  if (subject?.credential_id && hasDisplayValue(subject.credential_id)) {
    return String(subject.credential_id).trim();
  }

  if (
    responseData?.certificate_id &&
    hasDisplayValue(responseData.certificate_id)
  ) {
    return String(responseData.certificate_id).trim();
  }

  if (
    responseData?.certificateId &&
    hasDisplayValue(responseData.certificateId)
  ) {
    return String(responseData.certificateId).trim();
  }

  return null;
}

export function formatVerificationCreatedAt(createdAt) {
  if (!createdAt) {
    return null;
  }

  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) {
    return String(createdAt);
  }

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateDdMmYyyy(createdAt) {
  if (!createdAt) return null;
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return String(createdAt);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function buildApostilleDisplayData(subject, responseData) {
  const displayData = {};

  displayData["__section__apostille_section_convention"] = "__section__";

  // Fields 1–4
  APOSTILLE_CREDENTIAL_FIELDS.slice(0, 4).forEach(({ key, label }) => {
    const value = subject[key];
    if (hasDisplayValue(value)) {
      displayData[label] = value;
    }
  });

  // Section heading before the "Reconhecido" block
  displayData["__section__apostille_section_reconhecido"] = "__section__";

  // Field 5: nota_local_apostila
  const notaField = APOSTILLE_CREDENTIAL_FIELDS.find(
    (f) => f.key === "nota_local_apostila",
  );
  if (notaField && hasDisplayValue(subject[notaField.key])) {
    displayData[notaField.label] = subject[notaField.key];
  }

  // Field 6: a (le) date from issued_at (fallback created_at)
  const dateFormatted = formatDateDdMmYyyy(
    responseData?.issued_at || responseData?.created_at,
  );
  if (dateFormatted) {
    displayData["apostille_field_6"] = dateFormatted;
  }

  // Field 7: autoridade_emissora_da_apostila
  const autoridadeField = APOSTILLE_CREDENTIAL_FIELDS.find(
    (f) => f.key === "autoridade_emissora_da_apostila",
  );
  if (autoridadeField && hasDisplayValue(subject[autoridadeField.key])) {
    displayData[autoridadeField.label] = subject[autoridadeField.key];
  }

  // Field 8: sob o (sous) = certificate_id
  const certId = responseData?.certificate_id || subject?.credential_id;
  if (hasDisplayValue(certId)) {
    displayData["apostille_field_8"] = certId;
  }

  // Fields 9 & 10: selo_carimbo and assinatura shown side by side as previews
  const seloValue = subject["selo_carimbo"];
  const assinaturaValue = subject["assinatura"];
  if (hasDisplayValue(seloValue) || hasDisplayValue(assinaturaValue)) {
    displayData["__pair__9-10"] = {
      left: { i18nKey: "apostille_field_9", value: seloValue },
      right: { i18nKey: "apostille_field_10", value: assinaturaValue },
    };
  }

  return displayData;
}

function buildGenericCredentialSubjectDisplay(subject) {
  return Object.fromEntries(
    Object.entries(subject).filter(
      ([key, value]) =>
        !CREDENTIAL_SUBJECT_SKIP_KEYS.has(key) && hasDisplayValue(value),
    ),
  );
}

export function buildVcVerificationDisplayData(
  responseData,
  notAvailable = "Not Available",
) {
  const subject = responseData?.credential_subject;

  if (subject && typeof subject === "object" && !Array.isArray(subject)) {
    if (isApostilleCredentialSubject(subject)) {
      const apostilleData = buildApostilleDisplayData(subject, responseData);
      if (Object.keys(apostilleData).length > 0) {
        return apostilleData;
      }
    }

    const displayEntries = buildGenericCredentialSubjectDisplay(subject);
    if (Object.keys(displayEntries).length > 0) {
      return displayEntries;
    }
  }

  return {
    certificate_id: responseData?.certificate_id || notAvailable,
    programme_name: responseData?.programme_name || notAvailable,
    issuer: responseData?.issuer_name || notAvailable,
    candidate_name: responseData?.recipient_name || notAvailable,
    candidate_email: responseData?.recipient_email || notAvailable,
  };
}
